<template>
  <div
    class="relative w-full max-h-screen bg-gradient-to-br from-gradientStart to-gradientEnd px-0 py-0 laptop:px-6 laptop:py-6 desktop:px-6 large:px-8 ultra:px-12 overflow-x-hidden"
  >
    <div
      class="fixed bottom-0 right-0 z-0 w-[1074px] h-[859px] rounded-[1074px] opacity-50 blur-[250px] bg-gradient-to-br from-blurGradient-start via-blurGradient-mid2 to-blurGradient-end"
    ></div>

    <div
      class="fixed top-0 left-0 z-0 w-[418px] h-[213px] rounded-[418px] opacity-70 blur-[250px] bg-gradient-to-br from-blurGradient-start via-blurGradient-mid2 to-blurGradient-end"
    ></div>

    <aside
      :class="[
        'fixed top-6 bottom-6 bg-white shadow-md z-20 transition-all duration-300 rounded-xl overflow-y-auto',
        isSmallScreen ? 'hidden' : 'block',
        sidebarStore.isSidebarCollapsed
          ? 'w-[90px]'
          : 'w-[215px] desktop:w-[232px] large:w-[275px] ultra:w-[300px]',
      ]"
    >
      <LeftSidebar />
    </aside>

    <Sidebar
      v-model:visible="rightPanelStore.isRightPanelVisible"
      position="right"
      class="!w-[300px] mobile:!w-[330px] desktop:!w-[400px] ultra:!w-[474px] bg-white rounded-2xl"
      :showCloseIcon="false"
      :blockScroll="false"
      id="right-panel"
    >
      <div
        class="absolute w-[825px] h-[825px] -right-[574px] -bottom-[448px] md:-right-[430px] md:-bottom-[259px] rounded-[825px] opacity-30 blur-[250px] bg-gradient-to-br from-blurGradient-start via-blurGradient-mid2 to-blurGradient-end"
      ></div>
      <RightPanel />
    </Sidebar>

    <div
      :class="[
        'relative z-10 h-[calc(100vh-3rem)]',
        sidebarStore.isSidebarCollapsed
          ? isSmallScreen
            ? ''
            : 'ml-[calc(90px+1.5rem)] large:ml-[calc(90px+2rem)] ultra:ml-[calc(90px+3rem)]'
          : isSmallScreen
          ? ''
          : 'ml-[calc(215px+1.5rem)] desktop:ml-[calc(232px+1.5rem)] large:ml-[calc(275px+2rem)] ultra:ml-[calc(300px+3rem)]',
      ]"
    >
      <header class="h-16 shadow-sm sticky top-0 z-10 w-full">
        <Header id="header-navBar" />
      </header>
      <div
        :class="[
          'hide-scroll flex flex-col h-[calc(100vh-3rem-4rem)] overflow-y-auto',
          'md:h-[calc(100vh-3rem-4rem)]',
          'h-[calc(108vh-3rem-4rem-4rem)]',
        ]"
      >
        <main class="flex-1">
          <router-view />
        </main>
        <Footer
          v-if="route.meta.footer == true"
          @passphraseClick="onPassphraseClick"
          @subscribeClick="subscribeClick"
        />
        <CreatePassphraseDialog
          :visible="showPassphraseDialog"
          @update:visible="showPassphraseDialog = $event"
          @submit="onPassphraseCreate"
        />

        <SuccessDialog
          v-model:visible="showSuccessDialog"
          v-bind="successDialogProps"
        />
      </div>
    </div>

    <BottomNav :class="{ hidden: !isSmallScreen }" id="bottom-nav" />
  </div>

  <ConfirmationDialog
    v-model:visible="isConfirmSubscriptionDialogOpen"
    v-bind="confirmSubscriptionDialogProps"
    @ok="onConfirmSubscriptionDialogOk"
    @cancel="onConfirmSubscriptionDialogCancel"
  />

  <SuccessPromoCodeDialog
    v-model:modelValue="profileStore.isPromoCodeSuccessModalOpen"
  />
</template>

<script setup>
import LeftSidebar from "./LeftSidebar.vue";
import Header from "./Header.vue";
import RightPanel from "./RightPanel.vue";
import BottomNav from "./BottomNav.vue";
import { useRightPanelStore, useSidebarStore } from "@/stores/layoutStore";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import Footer from "./Footer.vue";
import CreatePassphraseDialog from "@/views/Security/dialogs/CreatePassphraseDialog.vue";
import {
  tourGuideStepTwo,
  tourGuideStepThree,
  tourGuideStepFour,
  tourGuideStepOne,
  closeSvg,
  checkIcon,
  nextIcon,
  prevIcon,
  successCheckIcon
} from "@/utils/commonSvg";
import { computed, onMounted, onUnmounted, provide, ref, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ROUTE_NAME, ROUTE_PATHS } from "@/router/constants";
import { useSecurityStore } from "@/stores/securityStore";
import { useUserStore } from "@/stores/userStore";
import { useProfileStore } from "@/stores/profileStore";
import SuccessPromoCodeDialog from "@/views/Subscription/SuccessPromoCodeDialog.vue";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
import { useSubscriptionLogic } from "@/composables/useSubscriptionLogic";
import { useI18n } from "vue-i18n";

// Store instances
const securityStore = useSecurityStore();
const userStore = useUserStore();
const profileStore = useProfileStore();
const rightPanelStore = useRightPanelStore();
const sidebarStore = useSidebarStore();
const { t } = useI18n();

onMounted(async () => {
   await rightPanelStore.getModulesData();
});

// State
const windowWidth = ref(window.innerWidth);
const isSmallScreen = ref(window.innerWidth < 1024);
const showSuccessDialog = ref(false);
const showPassphraseDialog = ref(false);

const {
  isConfirmSubscriptionDialogOpen,
  onConfirmSubscriptionDialogOk,
  onConfirmSubscriptionDialogCancel,
  confirmSubscriptionDialogProps,
} = useSubscriptionLogic();

// Router
const route = useRoute();
const router = useRouter();

// Responsive design detection for mobile/tablet breakpoints
// isMobile is now handled within useSubscriptionLogic for the dialog props

// Open passphrase modal
const onPassphraseClick = () => {
  showPassphraseDialog.value = true;
};

// Open subscription modal
const subscribeClick = () => {
  profileStore.isSubscriptionModalOpen = true;
};



// Save passphrase securely
const savePassphrase = async () => {
  try {
    const { encryptedPrivateKey, encryptedEncryptionKey, rsaPublicKey } =
      securityStore.encryptionInfo;
    let payload = {
      userId: userStore.loginUserId,
      encryptedKey: encryptedPrivateKey,
      encryptedEncryptionKey: encryptedEncryptionKey,
      publicKey: rsaPublicKey,
      encryptionKeyVersion: 0,
      hint: securityStore.hint,
    };
    await securityStore.saveEncryption(payload);
    showPassphraseDialog.value = false;
    showSuccessDialog.value = true;
  } catch (error) {
    console.error("Error for save Encryption:", error);
  }
};

// Handle passphrase creation
function onPassphraseCreate() {
  savePassphrase();
}

// Props for success dialog
const successDialogProps = computed(() => {
  return {
    title: t("securityModule.dialog.passphraseCreatedSuccessTitle"),
    subtitle: t("securityModule.dialog.passphraseCreatedSuccessSubtitle"),
    svg: successCheckIcon(),
    svgStyle: {
      background: "linear-gradient(90deg, #5EDCBF 0%, #9AEEF9 100%)",
    },
    subtitleClass: "",
    autoClose: true,
    duration: 3000,
    dialogClass:
      "bg-white rounded-2xl w-[95%] sm:w-[90%] md:w-[80%] lg:w-[70%] xl:w-[60%] max-w-[1051px] h-[80vh] sm:h-[85vh] md:h-[90vh] max-h-[694px]",
  };
});

// Additional breakpoint helper for mobile (used for more precise tour targeting)
const isMobile = computed(() => windowWidth.value < 640);

// Tour steps definition
const tourSteps = computed(() => [
  {
    // On true mobile (<640px), target the dedicated mobile card.
    // On tablet/desktop, target the main dashboard card so it always exists.
    element: isMobile.value ? "#dashboard-first-item-mobile" : "#dashboard-first-item",
    popover: {
      description: t('tourGuideLabels.tourGuideDashboard'),
      side: isSmallScreen.value ? "top" : "left",
      align: isSmallScreen.value ? "center" : "start",
      offset: 0,
      icon: tourGuideStepOne,
    },
  },
  {
    element: isSmallScreen.value ? "#bottom-nav" : "#sidebar-dashboard-item",
    popover: {
      description: isSmallScreen.value
        ? t('tourGuideLabels.tourGuideBottomNavbar')
        : t('tourGuideLabels.tourGuideSidebar'),
      side: isSmallScreen.value ? "top" : "right",
      align: "start",
      offset: 0,
      icon: tourGuideStepTwo,
    },
  },
  {
    element: "#header-navBar",
    popover: {
      description: t('tourGuideLabels.tourGuideNavigationBar'),
      side: "bottom",
      align: "start",
      offset: 0,
      icon: tourGuideStepThree,
    },
    onHighlightStarted: async () => {
      await new Promise((resolve) => setTimeout(resolve, 100));
    },
  },
  {
    element: "#right-panel",
    popover: {
      description: t('tourGuideLabels.tourGuideRightPanel'),
      side: "left",
      align: "center",
      icon: tourGuideStepFour,
    },
    onHighlightStarted: async () => {
      rightPanelStore.showRightPanel();
      await new Promise((resolve) => setTimeout(resolve, 100));
    },
  },
]);

// Helper function to check if dashboard is ready (extracted to reduce nesting)
const checkDashboardReady = () => {
  const dashboardElement = document.querySelector("#dashboard-first-item");
  if (!dashboardElement) return false;

  const hasContent =
    dashboardElement.querySelector(".bg-frost") !== null ||
    dashboardElement.querySelector('[class*="gradient"]') !== null;

  return hasContent;
};

// Helper function to handle dashboard ready event (extracted to reduce nesting)
const createDashboardReadyHandler = (resolve) => {
  return () => {
    setTimeout(() => {
      if (checkDashboardReady()) {
        window.removeEventListener("dashboardReady", handleDashboardReady);
        resolve();
      }
    }, 100);
  };
};

const startTour = async () => {
  if (rightPanelStore.isShowTour) {
    rightPanelStore.isShowTour = false;
  }
  
  const disableScroll = () => {
    document.body.style.overflow = "hidden";
    document.body.style.height = "100vh";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = "0";
    
    const scrollContainers = document.querySelectorAll(
      ".overflow-y-auto, .overflow-y-scroll, .scroll-thin"
    );
    scrollContainers.forEach((container) => {
      container.style.overflow = "hidden";
      container.dataset.originalOverflow = container.style.overflow;
    });
    document.documentElement.style.overflow = "hidden";
  };

  const enableScroll = () => {
    document.body.style.overflow = "";
    document.body.style.height = "";
    document.body.style.position = "";
    document.body.style.width = "";
    document.body.style.top = "";
    
    const scrollContainers = document.querySelectorAll(
      ".overflow-y-auto, .overflow-y-scroll, .scroll-thin"
    );
    scrollContainers.forEach((container) => {
      container.style.overflow = container.dataset.originalOverflow || "";
      delete container.dataset.originalOverflow;
    });
    document.documentElement.style.overflow = "";
  };

  const waitForDashboardReady = () => {
    return new Promise((resolve) => {
      // Check if already ready
      if (checkDashboardReady()) {
        resolve();
        return;
      }

      // Create event handler
      const handleDashboardReady = createDashboardReadyHandler(resolve);
      
      // Add event listener
      window.addEventListener("dashboardReady", handleDashboardReady);

      // Timeout fallback
      setTimeout(() => {
        window.removeEventListener("dashboardReady", handleDashboardReady);
        resolve();
      }, 5000);
    });
  };

  const recalculatePopoverPosition = (popover) => {
    if (!popover || !popover.wrapper) return;
    
    const wrapper = popover.wrapper;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const rect = wrapper.getBoundingClientRect();
    
    if (
      rect.left < 0 ||
      rect.right > viewportWidth ||
      rect.top < 0 ||
      rect.bottom > viewportHeight
    ) {
      let newLeft = rect.left;
      let newTop = rect.top;
      
      if (rect.left < 0) newLeft = 10;
      if (rect.right > viewportWidth) newLeft = viewportWidth - rect.width - 10;
      if (rect.top < 0) newTop = 10;
      if (rect.bottom > viewportHeight)
        newTop = viewportHeight - rect.height - 10;

      wrapper.style.left = `${newLeft}px`;
      wrapper.style.top = `${newTop}px`;
      wrapper.style.transform = "none";
    }
  };

  const startTourDriver = () => {
    disableScroll();
    
    // Create resize handler outside
    const handleTourResize = () => {
      const popover = document.querySelector(".driver-popover");
      if (popover) {
        recalculatePopoverPosition({ wrapper: popover });
      }
    };
    
    const tourDriver = driver({
      animate: true,
      opacity: 0.75,
      showButtons: ["next", "previous"],
      popoverClass: "driverjs-theme-custom",
      popoverOffset: 10,
      steps: tourSteps.value,

      onHighlighted: (element, step) => {
        if (step.element === "#header-navBar") {
          const el = document.querySelector("#header-navBar");
          if (el) {
            el.style.height = getComputedStyle(el).height;
          }
        }
      },
      
      onPopoverRender: (popover, { state }) => {
        const { activeIndex, activeStep } = state;
        const totalSteps = tourSteps.value?.length;
        const currentStep = activeIndex;
        const isFirstStep = currentStep === 0;
        const isLastStep = currentStep === totalSteps - 1;
        const stepData = activeStep?.popover || {};
        const { icon, description } = stepData;

        const customContent = document.createElement("div");
        customContent.className =
          "flex flex-col justify-end items-end gap-2 max-w-[312px]";

        let buttonsHTML = "";
        if (isFirstStep) {
          buttonsHTML = `
            <div class="flex items-center gap-0 justify-end">
              <button class="bg-transparent border-none text-deepSky text-shadow-none text-sm font-medium py-0.5 px-4 cursor-pointer transition-colors duration-200 no-underline flex items-center gap-1.5 min-w-[70px] justify-center driver-next-btn">
                ${t('commonLabels.next')} ${nextIcon({
            width: "24",
            height: "24",
            color: "#05A8F4",
          })}
              </button>
            </div>
          `;
        } else if (isLastStep) {
          buttonsHTML = `
            <div class="flex items-center gap-0 justify-end">
              <button class="bg-transparent border-none text-deepSky text-shadow-none text-sm font-medium py-0.5 px-4 cursor-pointer transition-colors duration-200 no-underline flex items-center gap-1.5 min-w-[70px] justify-center driver-prev-btn">
                ${prevIcon({ width: "24", height: "24", color: "#05A8F4" })} ${
            t('commonLabels.back')
          }
              </button>
            </div>
          `;
        } else {
          buttonsHTML = `
            <div class="flex items-center gap-0">
              <button class="bg-transparent border-none text-deepSky text-shadow-none text-sm font-medium py-0.5 px-4 cursor-pointer transition-colors duration-200 no-underline flex items-center gap-1.5 min-w-[70px] justify-center driver-prev-btn">
                ${prevIcon({ width: "24", height: "24", color: "#05A8F4" })} ${
            t('commonLabels.back')
          }
              </button>
              <div class="w-[1px] h-5 bg-white/20 m-0"></div>
              <button class="bg-transparent border-none text-deepSky text-shadow-none text-sm font-medium py-0.5 px-4 cursor-pointer transition-colors duration-200 no-underline flex items-center gap-1.5 min-w-[70px] justify-center driver-next-btn">
                ${t('commonLabels.next')} ${nextIcon({
            width: "24",
            height: "24",
            color: "#05A8F4",
          })}
              </button>
            </div>
          `;
        }

        customContent.innerHTML = `
          <div class="flex justify-end items-center mb-2 w-full">
            <div class="w-6 h-6 flex-shrink-0">${icon}</div>
          </div>
          <div class="text-white text-right text-base mb-4">${description}</div>
          <div class="flex flex-col items-stretch w-full">
            <div class="flex justify-between items-center w-full mb-0">
              <div class="flex items-center rounded-xl px-2 py-1 gap-0.5">
                <span class="text-xs text-white font-bold">${
                  currentStep + 1
                }</span>
                <span class="text-xs text-white font-bold">/</span>
                <span class="text-xs text-white font-bold">${totalSteps}</span>
              </div>
              ${buttonsHTML}
            </div>
            <div class="w-full h-px bg-white/10 my-2"></div>
            <div class="flex justify-end w-full">
              <button class="bg-transparent border-none text-shadow-none text-sm font-medium py-0.5 px-4 cursor-pointer transition-colors duration-200 no-underline flex items-center gap-1.5 min-w-[70px] justify-center driver-close-btn">
                <span class="flex items-center gap-1.5">
                  ${
                    isLastStep
                      ? checkIcon({
                          width: "16",
                          height: "16",
                          color: "#05A8F4",
                        })
                      : closeSvg({
                          width: "16px",
                          height: "16px",
                          color: "rgba(255, 255, 255, 0.6)",
                        })
                  }
                  <span class="${
                    isLastStep ? "text-deepSky" : "text-white opacity-60"
                  }" style="display:inline-block; font-size: 12px !important; font-weight: 400 !important;">
                    ${isLastStep ? t('commonLabels.done') : t('commonLabels.exit')}
                  </span>
                </span>
              </button>
            </div>
          </div>
        `;

        const existingContent = popover.wrapper.querySelector(
          ".driver-popover-description"
        );
        if (existingContent) {
          existingContent.innerHTML = "";
          existingContent.appendChild(customContent);
        }
        setTimeout(() => {
          recalculatePopoverPosition(popover);
        }, 100);

        setTimeout(() => {
          popover.wrapper
            .querySelector(".driver-prev-btn")
            ?.addEventListener("click", () => tourDriver.movePrevious());
          popover.wrapper
            .querySelector(".driver-next-btn")
            ?.addEventListener("click", async () => {
              if (currentStep + 1 === 3) {
                await rightPanelStore.showRightPanel();
              }
              tourDriver.moveNext();
            });
          popover.wrapper
            .querySelector(".driver-close-btn")
            ?.addEventListener("click", () => {
              enableScroll();
              tourDriver.destroy();
            });
        }, 10);
      },
      
      onDeselected: () => {
        const el = document.querySelector("#header-navBar");
        if (el) {
          el.style.height = "";
        }
        if (rightPanelStore.isRightPanelVisible) {
          rightPanelStore.hideRightPanel();
        }
      },
      
      onDestroyed: () => {
        enableScroll();
        window.removeEventListener("resize", handleTourResize);
      },
    });

    // Add resize listener
    window.addEventListener('resize', handleTourResize);
    
    // Start the tour
    tourDriver.drive();
  };

  if (route.name !== ROUTE_NAME.DASHBOARD) {
    await router.push(ROUTE_PATHS.DASHBOARD);
    await router.isReady();
    await nextTick();
  } else {
    await nextTick();
  }

  await waitForDashboardReady();
  startTourDriver();
};

// Provide tour start function globally
provide("startTour", startTour);

const openPromoCode = () => {
  profileStore.isPromoCodeModalOpen = true;
};

// Screen resize handler
const handleResize = () => {
  windowWidth.value = window.innerWidth;
  isSmallScreen.value = window.innerWidth < 1024;
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>
<style>
#driver-popover-content {
  background: #2a2b2b !important;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.5) !important;
  font-family: sans-serif !important;
}

.driver-popover-close-btn {
  display: none !important;
}

.driver-popover,
.driver-popover * {
  box-sizing: border-box !important;
  font-family: sans-serif !important;
}

.driver-popover-title,
.driver-popover-footer {
  display: none !important;
}

/* Arrow styles - consolidated with transforms */
#driver-popover-content .driver-popover-arrow-side-left.driver-popover-arrow {
  border-left-color: #2a2b2b;
  transform: translateX(0) !important;
  -webkit-transform: translateX(0) !important;
}

#driver-popover-content .driver-popover-arrow-side-right.driver-popover-arrow {
  border-right-color: #2a2b2b;
  transform: translateX(0) !important;
  -webkit-transform: translateX(0) !important;
}

#driver-popover-content .driver-popover-arrow-side-top.driver-popover-arrow {
  border-top-color: #2a2b2b;
  transform: translateY(0) !important;
  -webkit-transform: translateY(0) !important;
}

#driver-popover-content .driver-popover-arrow-side-bottom.driver-popover-arrow {
  border-bottom-color: #2a2b2b;
  transform: translateY(0) !important;
  -webkit-transform: translateY(0) !important;
}

.driver-overlay {
  pointer-events: auto !important;
  cursor: not-allowed !important;
}

.driver-overlay svg {
  -webkit-transform: translateZ(0) !important;
  transform: translateZ(0) !important;
}

.driver-active-element {
  pointer-events: none !important;
  z-index: 10000 !important;
  position: relative !important;
  -webkit-transform: translateZ(0) !important;
  transform: translateZ(0) !important;
  -webkit-backface-visibility: hidden !important;
  backface-visibility: hidden !important;
}

.driver-popover {
  pointer-events: auto !important;
  z-index: 10001 !important;
  position: fixed !important;
  -webkit-transform: translateZ(0) !important;
  transform: translateZ(0) !important;
  will-change: transform !important;
}

.driver-popover-wrapper {
  position: fixed !important;
  -webkit-backface-visibility: hidden !important;
  backface-visibility: hidden !important;
  -webkit-perspective: 1000px !important;
  perspective: 1000px !important;
}

body.driver-active {
  overflow: hidden !important;
  height: 100vh !important;
  position: fixed !important;
  width: 100% !important;
}

html.driver-active {
  overflow: hidden !important;
}

/* Safari-specific smooth rendering */
@supports (-webkit-appearance: none) {
  .driver-popover {
    -webkit-font-smoothing: antialiased !important;
    -moz-osx-font-smoothing: grayscale !important;
  }
}

/* Prevent Driver.js from breaking sticky headers */
#header-navBar.driver-active-element,
header.driver-active-element {
  position: sticky !important;
  top: 0 !important;
  height: 4rem !important;
  transform: none !important;
  -webkit-transform: none !important;
  z-index: 10000 !important;
}

#bottom-nav.driver-active-element {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  width: 100% !important;
  z-index: 10000 !important;
  transform: none !important;
  -webkit-transform: none !important;
}
</style>
